import { BehaviorSubject, Observable, Subscriber, Subscription, catchError, delay, of } from 'rxjs';
import { mockCustomerLoginResponse } from '../services/mockData/mock-data';
import { Inject, Injectable, OnDestroy, inject } from '@angular/core';
import { Event, NavigationEnd, Router } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { DOCUMENT } from '@angular/common';

const MOCK_LOGIN_RESPONSE: LoginResponse = {
    ".expires": 'FAKE_VALUE',
    ".issued": 'FAKE_VALUE',
    "as:client_id": 'FAKE_VALUE',
    access_token: 'FAKE_VALUE',
    email: 'FAKE_VALUE',
    expires_in: NaN,
    isAdmin: 'FAKE_VALUE',
    isInteraction: 'FAKE_VALUE',
    isMarketing: 'FAKE_VALUE',
    isTaskManager: 'FAKE_VALUE',
    mobile: 'FAKE_VALUE',
    pin: 'FAKE_VALUE',
    refresh_token: 'FAKE_VALUE',
    token_type: 'FAKE_VALUE',
    userName: 'FAKE_VALUE'
};

export declare enum LogoutReason {
    NONE = "NONE",
    USER = "USER",
    SESSION_EXPIRED = "SESSION_EXPIRED"
}

/**
 * Shared service for managing authentication state.  
 * Utilizes JWTs for authentication.  
 * Supports login, logout and checking authentication status.  
 */
@Injectable({
    providedIn: 'root'
})
export class AuthService implements OnDestroy {
    //#region Variables

    /**
     * Development mode bypass for authentication.
     * @deprecated This should be removed before production.
     */
    private devModeByPass: boolean = false;

    /**
     * BehaviorSubject for the authentication state.
     */
    private readonly _isAuthenticated: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

    /**
     * BehaviorSubject for the session restoration state.
     */
    private readonly _isRestoringSession: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

    /**
     * When set to true, the header will not be shown.
     * This will automatically reset when the page navigates.
     */
    public forceHideHeader: boolean = false;

    /**
     * SSR-safe reference to the local storage.
     */
    private localStorageRef: Storage | undefined = this.document.defaultView?.localStorage;

    /**
     * The subscription to track router events.
     */
    private sub: Subscription;

    /**
     * The base URL for the API.
     * @readonly
     */
    private get baseUrl(): string {
        return API_BASE_URL + '/api';
    }

    /**
     * Observable for the authentication state.
     */
    public get isAuthenticated$(): Observable<boolean> {
        return this._isAuthenticated.asObservable();
    }

    public get isAuthenticated(): boolean {
        return this._isAuthenticated.getValue();
    }

    public get isRestoringSession$(): Observable<boolean> {
        return this._isRestoringSession.asObservable();
    }

    public get usernameFromToken(): string {
        const previousLoginResponseStr: string | null | undefined = this.localStorageRef?.getItem('previousLoginResponse');

        if (!previousLoginResponseStr) {
            return '';
        }

        const parsed: LoginResponse = JSON.parse(previousLoginResponseStr) as LoginResponse;

        if (!parsed || !parsed.userName) {
            return '';
        }

        return parsed.userName;
    }

    public get bearerToken(): string {
        // Check that the user is still authenticated.
        this.checkAuthentication();

        // Get the token from local storage.
        const token: string | null | undefined = this.localStorageRef?.getItem('token');

        if (!token) {
            throw new Error('User unauthorized and unauthenticated.');
        }

        // Return the token as a Bearer token.
        return `Bearer ${token}`;
    }

    //#endregion

    private readonly router: Router = inject(Router);

    private readonly http: HttpClient = inject(HttpClient);

    constructor(@Inject(DOCUMENT) private readonly document: Document) {
        this.checkAuthentication();

        // Clear data when navigating to /login or /logout.
        this.sub = this.router.events
            .subscribe((event: Event) => {
                if (event instanceof NavigationEnd) {
                    this.forceHideHeader = false;

                    if (this.router.url.includes('/login') || this.router.url.includes('/logout')) {
                        this.clearStorage();
                    }
                }
            });
    }

    //#region Login/Logout Methods

    /**
     * Log the user in.
     * This is done by storing the token in local storage.
     * @deprecated - TODO: create abstract implementation.
     */
    public vendorLogin(username: string, password: string, client_id = 'optimal-vendor'): Promise<boolean> {
        const request: VendorLoginRequest = {
            username: username,
            password: password,
            client_id: client_id
        };

        if (this.devModeByPass) {
            this.storeToken(MOCK_LOGIN_RESPONSE);
            return Promise.resolve<boolean>(true);
        }

        return new Promise((resolve, reject) => {
            this.http.post(`${this.baseUrl}/account/vendorLogin`, request)
                .pipe(
                    catchError((error) => {
                        console.error(error);
                        reject(error);
                        return of([]);
                    })
                )
                .subscribe((response: any) => {
                    this.storeToken(response as LoginResponse);
                    // Redirect to active application.
                    resolve(true);
                });
        });
    }

    /**
     * Log an internal office user in.
     * Uses the generic /api/account/login endpoint (same as optimal-client).
     * @deprecated - TODO: create abstract implementation.
    */
    public officeLogin(username: string, password: string): Promise<boolean> {
        const request = {
            email: username,
            password: password
        };

        if (this.devModeByPass) {
            this.storeToken(MOCK_LOGIN_RESPONSE);
            return Promise.resolve<boolean>(true);
        }

        return new Promise((resolve, reject) => {
            this.http.post(`${this.baseUrl}/account/login`, request)
                .pipe(
                    catchError((error) => {
                        console.error(error);
                        reject(error);
                        return of([]);
                    })
                )
                .subscribe((response: any) => {
                    this.storeToken(response as LoginResponse);
                    resolve(true);
                });
        });
    }

    /**
     * Log the customer in.
     * @param referenceNumber The reference number of the customer.
     * @param idOrMobileNumber The ID or mobile number of the customer.
     * @deprecated - TODO: create abstract implementation.
    */
    public customerLogin(referenceNumber: string, idOrMobileNumber: string): Promise<boolean> {
        if (this.devModeByPass) {

            return new Promise((resolve) => {
                const mockResponse: Partial<CustomerLoginResponse> = {
                    ...mockCustomerLoginResponse,
                };
                this.localStorageRef?.setItem('applicationId', JSON.stringify((<CustomerLoginResponse>mockResponse).applicationId));

                // Redirect to active application.
                resolve(true);
            });

        } else {
            const request: CustomerLoginRequest = {
                referenceNumber: referenceNumber,
                idNumber: idOrMobileNumber
            };

            return new Promise((resolve, reject) => {
                this.http.post<CustomerLoginResponse>(`${this.baseUrl}/account/customerLogin`, request)
                    .pipe(catchError((error) => {
                        console.error(error);
                        reject(error);
                        return error;
                    }))
                    .subscribe((response: unknown | CustomerLoginResponse) => {
                        if (!response) {
                            return;
                        }

                        this.localStorageRef?.setItem('applicationId', JSON.stringify((<CustomerLoginResponse>response).applicationId));

                        this.localStorageRef?.setItem('referenceNumber', referenceNumber);

                        this.storeToken(response as CustomerLoginResponse);

                        // Redirect to active application.
                        resolve(true);
                    });
            });
        }
    }

    /**
     * Log the user out.
     * This is done by removing the token from local storage.
     */
    public logout(): void {
        this.clearStorage();

        this.checkAuthentication();
    }

    /**
     * Clear the local storage.
     */
    private clearStorage() {
        this.localStorageRef?.removeItem('token');

        this.localStorageRef?.removeItem('previousLoginResponse');

        this.localStorageRef?.removeItem('applicationId');

        this.localStorageRef?.removeItem('referenceNumber');
    }

    /**
     * Store the token in local storage.
     */
    private storeToken(response: LoginResponse) {
        const token: string = response.access_token;

        this.localStorageRef?.setItem('token', token);

        this.localStorageRef?.setItem('previousLoginResponse', JSON.stringify(response));

        this.checkAuthentication();
    }

    //#endregion
    //#region Validation Methods

    /**
     * Check if the user is authenticated.
     * This is done by checking if the token is expired.
     */
    public checkAuthentication(): void {
        if (this.isTokenExpired()) {
            this._isAuthenticated.next(false);

            this.router.navigate(['/login']);

            this.clearStorage();
        }
        else {
            this._isAuthenticated.next(true);
        }

        this._isRestoringSession.next(false);
    }

    /**
     * Check if the token is expired.
     */
    private isTokenExpired(): boolean {
        this._isRestoringSession.next(true);

        if (this.devModeByPass) {
            return false;
        }

        const previousLoginResponseStr: string | null | undefined = this.localStorageRef?.getItem('previousLoginResponse');

        if (!previousLoginResponseStr) {
            return true;
        }

        const parsed: LoginResponse = JSON.parse(previousLoginResponseStr) as LoginResponse;

        if (!parsed || !parsed.access_token) {
            return true;
        }

        const dateOfExpiry: Date = new Date(parsed['.expires']);

        return dateOfExpiry < new Date();
    }

    //#endregion
    //#region Forgot Password/Username Methods

    public forgotPassword(username: string) {
        if (USE_MOCKS) {
            return this.mockRequest({ username });
        }
        else {
            return this.http.post(`${this.baseUrl}/account/forgotPassword`, { username })
                .pipe(catchError((error) => {
                    console.error(error);
                    return error;
                }));
        }
    }

    public forgotUsername(email: string) {
        if (USE_MOCKS) {
            return this.mockRequest({ email });
        }
        else {
            return this.http.post(`${this.baseUrl}/account/forgotUsername`, { email })
                .pipe(catchError((error) => {
                    console.error(error);
                    return error;
                }));
        }
    }

    //#endregion

    //#region Mock API Helpers

    public mockRequest<T>(data?: T, extendDelayMs?: number): Observable<T> {
        // Random delay to simulate network latency between 500ms and 2500ms
        const randomDelay: number = Math.floor(Math.random() * 2000) + 500 + (extendDelayMs || 0);

        return (of(data) as Observable<T>)
            .pipe(delay(randomDelay));
    }

    /**
     * Creates an observable that emits the provided data.
     *
     * @template T The type of the data to emit.
     * @param {T} [data] The data to emit.
     * @return {Observable<T>} The observable with potential data.
     */
    protected mockObs<T>(data?: T, throwError = false): Observable<T> {
        return new Observable((observer: Subscriber<T>) => {

            // Random delay to simulate API latency, between 500ms and 2500ms.
            const mockApiRuntime: number = Math.floor(Math.random() * 2000) + 500;

            setTimeout(() => {

                // Throw an error if requested.
                if (throwError) {
                    observer.error('An error occurred.');
                    return;
                }

                // Emit the data if it is provided.
                if (data) observer.next(data);
                // Otherwise emit nothing.
                else observer.next();

                // Complete the observable.
                observer.complete();

            }, mockApiRuntime);

        });
    }

    //#endregion

    public ngOnDestroy(): void {
        this.sub.unsubscribe();
    }
}

interface LoginResponse {
    access_token: string;
    token_type: string;
    expires_in: number;
    refresh_token: string;
    'as:client_id': string;
    userName: string;
    isAdmin: string;
    isTaskManager: string;
    isInteraction: string;
    isMarketing: string;
    pin: string;
    email: string;
    mobile: string;
    '.issued': string;
    '.expires': string;
}

interface CustomerLoginResponse extends LoginResponse {
    applicationId: number;
}

interface CustomerLoginRequest {
    referenceNumber: string;
    idNumber: string;
}

interface VendorLoginRequest {
    username: string;
    password: string;
    client_id: string;
}